import { useState, type ReactNode } from 'react'
import { CollapsibleSection } from '../CollapsibleSection.tsx'

export const PAGE_SIZE = 25

/**
 * US-12's histories page by growing the query's limit rather than by holding a
 * cursor: these lists are live, so a drink ordered mid-session has to appear at
 * the top of a newest-first list, which a frozen cursor cannot do. Zero serves
 * already-synced rows from the local store, so paging forward is instant on a
 * warm cache.
 */
export function usePagedLimit(): { limit: number; showMore: () => void } {
  const [limit, setLimit] = useState(PAGE_SIZE)
  return { limit, showMore: () => setLimit((current) => current + PAGE_SIZE) }
}

/**
 * Chrome shared by every history section. The total row count is deliberately
 * not queried — counting needs the unbounded sync that paging exists to avoid —
 * so a full page implies there may be more.
 */
export function PagedSection({
  title,
  shown,
  limit,
  showMore,
  complete,
  emptyText,
  footer,
  children,
}: {
  title: string
  shown: number
  limit: number
  showMore: () => void
  complete: boolean
  emptyText: string
  /** Rendered whether or not the list has rows — e.g. a mutation's result. */
  footer?: ReactNode
  children: ReactNode
}) {
  const hasMore = shown >= limit

  return (
    <CollapsibleSection title={title} count={`${shown}${hasMore ? '+' : ''}`}>
      <div className="card">
        {shown === 0 ? <p className="empty">{emptyText}</p> : children}
        {footer}
        {!complete ? <p className="form-message">Syncing…</p> : null}
        {hasMore ? (
          <button className="btn btn-secondary btn-sm show-more" onClick={showMore}>
            Show more
          </button>
        ) : null}
      </div>
    </CollapsibleSection>
  )
}
